import React, { useEffect, useState } from 'react';
import {
    Box, Button, Modal, Table, TableBody, TableContainer,
    TableHead, TableRow, Paper, TablePagination,
    Typography, CircularProgress, TextField,
    InputAdornment, Menu, MenuItem, IconButton, Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from 'react-toastify';
import Papa from "papaparse";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getLearnerList } from '../../../services/mentorServices/ReviewServices.js';


const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: "#7e7e7e",
        color: theme.palette.common.white,
        textTransform: "capitalize",
        fontSize: 16,
        fontWeight: 600,
        whiteSpace: "nowrap"
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        textTransform: "capitalize",
        whiteSpace: "nowrap"
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd),&:nth-of-type(even)": {
        backgroundColor: theme.palette.action.hover,
        borderBottom: "1px solid rgba(224, 224, 224, 1)",
    },
    "&:last-child td, &:last-child th": {
        border: 0,
    },
}));

function highlightText(text, search) {
    if (!search || !text) return text;
    const parts = text.toString().split(new RegExp(`(${search})`, "gi"));
    return parts.map((part, index) =>
        part.toLowerCase() === search.toLowerCase() ? (
            <span key={index} style={{ backgroundColor: "yellow" }}>
                {part}
            </span>
        ) : (
            part
        )
    );
}

function AdminTotalRegisteredModal({ onClose, open, selected }) {
    // selected should contain { coe, labId } when opening this modal
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [learners, setLearners] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [anchorEl, setAnchorEl] = useState(null);

    useEffect(() => {
        if (!open) return;

        const fetchData = async () => {
            try {
                setLoading(true);
                setLearners([]);

                if (!selected?.coe || !selected?.labId) return;

                const response = await getLearnerList(selected.coe, selected.labId);

                // API returns array of objects with { learner, coeCenter, lab, trackScores }
                const rawList = response?.data?.data || [];

                // Flatten into a clean list for table display
                const mapped = rawList.map((item) => ({
                    learner_id: item?.learner?.learner_id,
                    roll_num: item?.learner?.roll_num || "-",
                    learnerName: item?.learner?.name || "-",
                    email: item?.learner?.email || "-",
                    mobile: item?.learner?.mobile || "-",
                    stream: item?.learner?.stream || "-",
                    current_semester: item?.learner?.current_semester ?? "-",
                    enrolment_year: item?.learner?.enrolment_year ?? "-",
                    learner_code: item?.learner?.learner_code || "-",
                    lab_name: item?.lab?.lab_name || "-",
                    lab_type: item?.lab?.lab_type || "-",
                    coe_name: item?.coeCenter?.name || "-",
                    groupName: item?.learner?.groupName || "-",
                    passout_year: item?.learner?.passout_year ?? "-",
                }));

                setLearners(mapped);
            } catch (error) {
                toast.error(
                    error?.response?.data?.message ||
                    error?.message ||
                    "Error fetching learner list"
                );
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [open, selected?.coe, selected?.labId]);

    // Client-side search across all relevant fields
    const filteredData = learners.filter((item) => {
        const value = search.toLowerCase();
        return (
            item?.roll_num?.toLowerCase().includes(value) ||
            item?.learnerName?.toLowerCase().includes(value) ||
            item?.email?.toLowerCase().includes(value) ||
            item?.mobile?.toLowerCase().includes(value) ||
            item?.stream?.toLowerCase().includes(value) ||
            item?.lab_name?.toLowerCase().includes(value) ||
            item?.lab_type?.toLowerCase().includes(value) ||
            item?.coe_name?.toLowerCase().includes(value) ||
            item?.learner_code?.toLowerCase().includes(value) ||
            item?.groupName?.toLowerCase().includes(value)
        );
    });

    const paginatedRows = filteredData.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const handleChangePage = (_, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    const handleDownloadClick = (e) => setAnchorEl(e.currentTarget);
    const handleCloseMenu = () => setAnchorEl(null);

    const handleDownload = (type) => {
        handleCloseMenu();

        if (!filteredData.length) {
            toast.warning("No data to export!");
            return;
        }

        const formatted = filteredData.map((r) => ({
            "Roll No": r.roll_num,
            "Student Name": r.learnerName,
            "Email": r.email,
            "Mobile": r.mobile,
            "Stream": r.stream,
            "Semester": r.current_semester,
            "Enrolment Year": r.enrolment_year,
            "Passout Year": r.passout_year,
            "Group": r.groupName,
            "Lab Name": r.lab_name,
            "Lab Type": r.lab_type,
            "COE": r.coe_name,
        }));

        if (type === "csv") {
            const csv = Papa.unparse(formatted);
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = "Total_Registered_Students.csv";
            link.click();
        }

        if (type === "pdf") {
            const doc = new jsPDF({ orientation: "landscape" });
            autoTable(doc, {
                startY: 20,
                head: [Object.keys(formatted[0])],
                body: formatted.map((r) => Object.values(r)),
                theme: "grid",
                headStyles: { fillColor: [126, 126, 126] },
                styles: { fontSize: 8, halign: "center" },
                didDrawPage: () => {
                    doc.setFontSize(12);
                    doc.text("Total Registered Students Report", 14, 15);
                }
            });
            doc.save("Total_Registered_Students.pdf");
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={{
                outline: "none !important",
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '90%',
                bgcolor: 'background.paper',
                boxShadow: 24,
                p: 4,
                borderRadius: 2,
                maxHeight: "90vh",
                overflow: "hidden",
            }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" pb={2}>
                    <Box>
                        <Typography
                            className="dashboard-header-title"
                            sx={{ whiteSpace: "nowrap" }}
                        >
                            Total Registered Students
                        </Typography>

                        {/* Summary info */}
                        {!loading && (
                            <Typography variant="body2" color="text.secondary" mt={0.5}>
                                Lab: <strong>{learners[0]?.lab_name || "-"}</strong>
                                &nbsp;|&nbsp; COE: <strong>{learners[0]?.coe_name || "-"}</strong>
                                &nbsp;|&nbsp; Total: <strong>{learners.length}</strong>
                            </Typography>
                        )}
                    </Box>

                    <Box display="flex" alignItems="center" gap={2}>
                        <TextField
                            size="small"
                            placeholder="Search by name, roll no, email, lab..."
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(0);
                            }}
                            sx={{ minWidth: "280px" }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                )
                            }}
                        />
                        <Box>
                            <Button
                                variant="contained"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadClick}
                            >
                                Download
                            </Button>
                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={handleCloseMenu}
                            >
                                <MenuItem onClick={() => handleDownload("csv")}>Download CSV</MenuItem>
                                <MenuItem onClick={() => handleDownload("pdf")}>Download PDF</MenuItem>
                            </Menu>
                        </Box>
                        <Tooltip title="Close">
                            <IconButton onClick={onClose} size="small" sx={{ ml: 1 }}>
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <TableContainer
                            component={Paper}
                            sx={{ maxHeight: "calc(90vh - 150px)", overflow: "auto" }}
                        >
                            <Table stickyHeader sx={{ tableLayout: "auto", width: "max-content", minWidth: "100%" }}>
                                <TableHead>
                                    <TableRow>
                                        {[
                                            "Roll No",
                                            "Student Name",
                                            "Email",
                                            "Mobile",
                                            "Stream",
                                            "Semester",
                                            "Enrolment Year",
                                            "Group",
                                            "Lab Name",
                                            "Lab Type",
                                        ].map((header) => (
                                            <StyledTableCell key={header}>
                                                {header}
                                            </StyledTableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {paginatedRows.length ? (
                                        paginatedRows.map((row, idx) => (
                                            <StyledTableRow key={idx}>
                                                <StyledTableCell>
                                                    {highlightText(row.roll_num, search)}
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    {highlightText(row.learnerName, search)}
                                                </StyledTableCell>
                                                <StyledTableCell sx={{ textTransform: "none" }}>
                                                    {highlightText(row.email, search)}
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    {highlightText(row.mobile, search)}
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    {highlightText(row.stream, search)}
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    {row.current_semester}
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    {row.enrolment_year}
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    {highlightText(row.groupName, search)}
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    {highlightText(row.lab_name, search)}
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    {highlightText(row.lab_type, search)}
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <StyledTableCell colSpan={10} align="center">
                                                No records found.
                                            </StyledTableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            rowsPerPageOptions={[10, 20, 30, 60, 100]}
                            component="div"
                            count={filteredData.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </>
                )}
            </Box>
        </Modal>
    );
}

export default AdminTotalRegisteredModal;
